import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';
import { useLanguage } from '../../Context/LanguageContext';

import { ReactComponent as CalanderSvg } from '../../image/Calander.svg';
import { ReactComponent as LeftArrowSvg } from '../../image/LeftArrow.svg';
import { ReactComponent as DoubleLeftArrowSvg } from '../../image/DoubleLeftArrow.svg';
import { ReactComponent as ExitSvg } from '../../image/BlackExit.svg';

export default function CalenderInput(props) {
  const { value, setValue, className, placeholder, clickFn, disabled, width } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [today, setToday] = useState(moment(value, 'YYYY.MM.DD'));
  const [isClicked, setIsClicked] = useState(false);
  const [dateList, setDateList] = useState([]);

  const TopLevelRef = useRef();
  const CalenderRef = useRef();

  const firstWeek = today.clone().startOf('month').week();
  const lastWeek = today.clone().endOf('month').week() === 1 ? 53 : today.clone().endOf('month').week();
  
  // 날짜 정리 //
  useEffect(() => {
    if (value === '') return;

    setToday(moment(value, 'YYYY.MM.DD'));
  }, [value])

  // 날짜기준 데이터 만들기 //
  useEffect(() => {
    let newDateList = [];

    for (let week = firstWeek; week <= lastWeek; week++) {
      for (let i = 0; i < 7; i++) {
        let days = today.clone().startOf('year').week(week).startOf('week').add(i, 'day');
        
        newDateList.push(days);
      } 
    }
    setDateList(newDateList);

    if (isClicked) {
      CalenderRef.current.style.height = `${118 + 56 * (newDateList.length / 7)}px`;
      CalenderRef.current.style.border = `1px solid ${theme.primary}`;
      CalenderRef.current.style.zIndex = `2`;
      CalenderRef.current.style.opacity = `1`;
    }
    else {
      CalenderRef.current.style.height = `0px`;
      CalenderRef.current.style.border = `0px solid ${theme.primary}`;
      CalenderRef.current.style.zIndex = `-3`;
      CalenderRef.current.style.opacity = `0`;
    }
  }, [isClicked, today, firstWeek, lastWeek]) 

  // 컴포넌트 외부 클릭시 이벤트를 받기위한 리스너 연결
  useEffect(() => {
    document.addEventListener("mousedown", onClickOutSide);

    return () => {
      document.removeEventListener("mousedown", onClickOutSide);
    }
  }, [])

  // 컴포넌트 외부 클릭시 실행
  function onClickOutSide(e) {
    if (TopLevelRef.current !== e.target && !TopLevelRef.current.contains(e.target)) {
        setIsClicked(false);
    }
  }

  function inputClick() {
    if (disabled) return;
    
    setIsClicked(!isClicked);
    // clickFn(className);
  }

  function dateClick(index) {
    setValue(dateList[index].format('YYYY.MM.DD'));
    setIsClicked(false);
  }

  return (
    <TopLevelWrapper ref={TopLevelRef} width={width}>
      <Input onClick={inputClick} disabled={disabled} width={width}>
        <Text black={value !== '' ? true : false}>
          {
            value !== '' ? moment(value, 'YYYY.MM.DD').format(t('calendarInput.fullDateFormat')) : placeholder
          }
        </Text>
        <StyledCalander />
      </Input>
      <CalenderView ref={CalenderRef}>
        <ButtonWrapper>
          <TitleWrapper>
            <ArrowButton onClick={() => setToday(today.clone().subtract(1, 'year'))} style={{ margin: `0 -7px 0 0` }}>
              <StyledDoubleArrow />
            </ArrowButton>
            <ArrowButton onClick={() => setToday(today.clone().subtract(1, 'month'))}>
              <StyledArrow />
            </ArrowButton>
            <Title>{today.format(t('calendarInput.calendarFormat'))}</Title>
            <ArrowButton onClick={() => setToday(today.clone().add(1, 'month'))}>
              <StyledArrow isRight />
            </ArrowButton>
            <ArrowButton onClick={() => setToday(today.clone().add(1, 'year'))} style={{ margin: `0 0 0 -7px` }}>
              <StyledDoubleArrow isRight />
            </ArrowButton>
          </TitleWrapper>
          <ExitButton onClick={() => setIsClicked(false)}>
            <StyledExitIcon color={theme.text} />
          </ExitButton>
        </ButtonWrapper>
        <DateColumnWrapper>
          <DateColumn style={{ width: `25px`, marginRight: `36px` }}>SUN</DateColumn>
          <DateColumn style={{ width: `30px`, marginRight: `34px` }}>MON</DateColumn>
          <DateColumn style={{ width: `23px`, marginRight: `36px` }}>TUE</DateColumn>
          <DateColumn style={{ width: `28px`, marginRight: `35px` }}>WED</DateColumn>
          <DateColumn style={{ width: `25px`, marginRight: `42px` }}>THU</DateColumn>
          <DateColumn style={{ width: `18px`, marginRight: `40px` }}>FRI</DateColumn>
          <DateColumn style={{ width: `24px`, marginRight: `28px` }}>SAT</DateColumn>
        </DateColumnWrapper>
        <DateWrapper>
          {
            dateList.map((data, index) => (
              data.format('MM') === today.format('MM') ?
              <Date key={index} onClick={() => dateClick(index)}>
                  {data.format('D')}
              </Date>
              :
              <WithoutHoverDate key={index}>
                  {data.format('D')}
              </WithoutHoverDate>
            ))
          }
        </DateWrapper>
      </CalenderView>
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: ${props => props.width ? 'auto' : '100%'};
`;

const Input = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 14px 10px;

  width: ${props => props.width ? `calc(${props.width}px - 18px)` : 'calc(100% - 18px)'};

  border: 1px solid ${props => props.theme.line};
  border-radius: 10px;
  background-color: ${props => props.disabled ? props.theme.line : props.theme.subBackground};

  gap: 5px;

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const Text = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.black ? props.theme.text : props.theme.subText};

  margin: 0 0 0 0px;
`;

const CalenderView = styled.div`
  position: absolute;

  display: flex;
  flex-direction: column;

  margin-top: 38px;

  border: 0px solid ${props => props.theme.primary};
  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  z-index: -30;
  opacity: 0;
  
  overflow: hidden;

  transition: all 0.3s ease-in-out;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: calc(100% - 56px);

  margin-top: 28px;
  padding: 0 28px 0 28px;
`;

const ExitButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 24px;
  height: 24px;

  &:hover {
    cursor: pointer;
  }
`;

const StyledExitIcon = styled(ExitSvg)`
  width: 20px;
  height: 20px;

  color: ${props => props.color};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 6px;
`;

const ArrowButton = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 24px;
  height: 24px;

  &:hover {
    cursor: pointer;
  }
`;

const StyledArrow = styled(LeftArrowSvg)`
  width: 8px;
  height: auto;
  flex-shrink: 0;

  color: ${props => props.theme.subText};
  transform: ${props => props.isRight ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const StyledDoubleArrow = styled(DoubleLeftArrowSvg)`
  width: 16px;
  height: auto;
  flex-shrink: 0;

  color: ${props => props.theme.subText};
  transform: ${props => props.isRight ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const Title = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  &:hover {
    cursor: default;
  }
`;

const DateColumnWrapper = styled.div`
  display: flex;
  flex-direction: row;

  margin-top: 29px;
  margin-left: 28px;
  margin-bottom: 23px;
`;

const DateColumn = styled.p`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  font-family: Poppins-bold;
  font-size: 12px;

  color: ${props => props.theme.primary};

  margin: 0 0 0 0;
`;

const DateWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  width: 448px;
  margin-left: 8px;
`;

const Date = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 48px;
  height: 48px;

  border-radius: 48px;

  margin: 0 7px 5px 7px;

  background-color: ${props => props.theme.subBackground};

  font-family: Poppins-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.primary};
    color: ${props => props.theme.white};

    font-family: Poppins-Bold;
    font-size: 14px;
  }
`;

const WithoutHoverDate = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 48px;
  height: 48px;

  border-radius: 48px;

  margin: 0 7px 5px 7px;

  background-color: ${props => props.theme.subBackground};

  font-family: Poppins-Regular;
  font-size: 14px;

  color: ${props => props.theme.line};

  &:hover {
    cursor: default;
  }
`;

const StyledCalander = styled(CalanderSvg)`
  flex-shrink: 0;
  width: 18px;
  height: 18px;

  & path {
    fill: ${props => props.theme.text};
  }
`;